/**
 * Module dependencies.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Badge } from 'app/components/ui/badge';
import { Button } from 'app/components/ui/button';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { appRoutes } from 'app/routes';
import { cn } from 'app/lib/utils';
import { Pause, Play, RefreshCw, Settings as SettingsIcon } from 'lucide-react';
import { AreaTrendChart, chartColors, chartPalette } from 'app/components/organisms/charts/charts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select';
import { CryptoBotCycleChart } from './crypto-bot-cycle-chart';

/**
 * Equity-chart timeframes (persisted). Backend downsamples snapshots to these.
 */

const TIMEFRAMES = ['5m', '15m', '30m', '1h', '4h', '12h', '1d', '3d', '1w', '1mo', '1y'];
const TIMEFRAME_KEY = 'cryptoBot.overview.timeframe';
import {
  useCryptoBotBtcCycle,
  useCryptoBotConfig,
  useCryptoBotOverview,
  useResetCryptoBotDryRun,
  useUpdateCryptoBotConfig
} from 'app/hooks/requests/crypto-bot/use-crypto-bot';
import { useCryptoGestPriceStream } from 'app/hooks/requests/crypto-gest/use-crypto-gest';

/**
 * Format a money amount in the pool's quote asset.
 */

function money(value: number | null | undefined, quote: string): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${quote}`;
}

/**
 * A labelled statistic card.
 */

function Stat({ label, value, tone }: { label: string; value: React.ReactNode; tone?: 'pos' | 'neg' }): JSX.Element {
  return (
    <div className={'rounded-xl border border-border bg-card p-4 shadow-sm'}>
      <div className={'text-[10px] uppercase tracking-wide text-muted-foreground'}>{label}</div>

      <div
        className={cn(
          'mt-1 text-lg font-semibold tabular-nums',
          tone === 'pos' && 'text-emerald-600',
          tone === 'neg' && 'text-destructive',
          !tone && 'text-foreground'
        )}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Export `CryptoBotOverview` component.
 */

export function CryptoBotOverview(): JSX.Element {
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [timeframe, setTimeframe] = React.useState<string>(() => {
    try {
      return window.localStorage.getItem(TIMEFRAME_KEY) || '1h';
    } catch {
      return '1h';
    }
  });

  const changeTimeframe = (value: string) => {
    setTimeframe(value);

    try {
      window.localStorage.setItem(TIMEFRAME_KEY, value);
    } catch {
      // ignore storage errors
    }
  };

  const { data: config, isLoading } = useCryptoBotConfig();
  const { data: overview } = useCryptoBotOverview(timeframe);
  const { data: cycle } = useCryptoBotBtcCycle();
  const update = useUpdateCryptoBotConfig();
  const reset = useResetCryptoBotDryRun();

  // Live prices per asset, pushed from the price stream (real-time updates).
  const holdingAssets = useMemo(() => (overview?.holdings ?? []).map(holding => holding.asset), [overview?.holdings]);
  const livePrices = useCryptoGestPriceStream(holdingAssets);

  const onReset = async () => {
    try {
      await reset.mutateAsync();
      success({ message: translate('cryptoBot.overview.resetDone') });
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  const equityData = useMemo(
    () =>
      (overview?.equityCurve ?? []).map(point => ({
        equity: point.equity,
        ...(point.breakdown ?? {}),
        label: new Date(point.at.replace(' ', 'T')).toLocaleString('en-GB', {
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          month: '2-digit'
        })
      })),
    [overview?.equityCurve]
  );

  const equitySeries = useMemo(
    () => [
      { color: chartColors.primary, key: 'equity', label: translate('cryptoBot.overview.equity') },
      ...(overview?.equityCurveAssets ?? []).map((asset, index) => ({
        color: chartPalette[index % chartPalette.length],
        key: asset,
        label: asset
      }))
    ],
    [overview?.equityCurveAssets, translate]
  );

  const toggleEnabled = async () => {
    if (!config) {
      return;
    }

    try {
      await update.mutateAsync({ enabled: !config.enabled });
      success({ message: translate(config.enabled ? 'cryptoBot.overview.paused' : 'cryptoBot.overview.started') });
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  if (isLoading || !config) {
    return (
      <div className={'grid place-items-center py-20'}>
        <Spinner />
      </div>
    );
  }

  const quote = config.quoteAsset;

  // Recompute values and drift from the live streamed prices so the cards move
  // with the price socket (~5s), not just the slower overview poll. Uses the same
  // target formula as the engine, so the drift matches what the bot acts on.
  const isRebalance = !!overview?.isRebalance;
  const cryptoPctRaw =
    config.strategy === 'rebalance'
      ? Number((config.strategyParams as any)?.targetPct ?? 50)
      : Number((config.strategyParams as any)?.cryptoPct ?? 50);
  const cryptoPct = Math.min(0.95, Math.max(0.05, cryptoPctRaw / 100));

  const baseHoldings = overview?.holdings ?? [];
  const liveValueOf = (holding: { asset: string; qty: number; value: number }) => {
    const price = livePrices[holding.asset];

    return price != null && holding.qty > 0 ? price * holding.qty : holding.value;
  };

  const usdcBalance = overview?.usdc?.balance ?? 0;
  const cryptoValue = baseHoldings.reduce((sum, holding) => sum + liveValueOf(holding), 0);
  const total = usdcBalance + cryptoValue;
  const perTarget = config.strategy === 'rebalance' ? cryptoPct : cryptoPct / Math.max(1, baseHoldings.length);

  const holdings = baseHoldings.map(holding => {
    const value = liveValueOf(holding);

    if (!isRebalance) {
      return { ...holding, value };
    }

    const targetValue = total * perTarget;

    return {
      ...holding,
      value,
      targetValue,
      diffValue: value - targetValue,
      diffPct: targetValue > 0 ? ((value - targetValue) / targetValue) * 100 : 0
    };
  });

  let usdcLive = overview?.usdc ?? null;

  if (usdcLive && isRebalance) {
    const targetValue = total * (1 - cryptoPct);

    usdcLive = {
      balance: usdcBalance,
      targetValue,
      diffValue: usdcBalance - targetValue,
      diffPct: targetValue > 0 ? ((usdcBalance - targetValue) / targetValue) * 100 : 0
    };
  }

  const cryptoTarget = holdings.reduce((sum, holding) => sum + ((holding as any).targetValue ?? 0), 0);
  const cryptoDiffValue = cryptoValue - cryptoTarget;
  const cryptoDiffPct = cryptoTarget > 0 ? (cryptoDiffValue / cryptoTarget) * 100 : null;

  const pnlTone =
    overview && overview.realizedPnl > 0 ? 'pos' : overview && overview.realizedPnl < 0 ? 'neg' : undefined;
  const retTone =
    overview?.totalReturnPct != null
      ? overview.totalReturnPct > 0
        ? 'pos'
        : overview.totalReturnPct < 0
        ? 'neg'
        : undefined
      : undefined;

  return (
    <div className={'w-full space-y-5'}>
      <div className={'flex flex-wrap items-center justify-between gap-3'}>
        <div className={'flex items-start gap-3'}>
          <div>
            <div className={'flex items-center gap-3'}>
              <Title className={'pb-0'}>{translate('cryptoBot.overview.title')}</Title>

              <Badge variant={config.enabled ? 'default' : 'secondary'}>
                {translate(config.enabled ? 'cryptoBot.status.running' : 'cryptoBot.status.paused')}
              </Badge>

              <Badge variant={config.mode === 'live' ? 'destructive' : 'outline'}>
                {translate(`cryptoBot.mode.${config.mode}`)}
              </Badge>
            </div>

            <p className={'mt-1 text-sm text-muted-foreground'}>{translate('cryptoBot.overview.subtitle')}</p>
          </div>
        </div>

        <div className={'flex items-center gap-2'}>
          <Button disabled={update.isLoading} onClick={toggleEnabled} variant={config.enabled ? 'outline' : 'default'}>
            {config.enabled ? <Pause className={'h-4 w-4'} /> : <Play className={'h-4 w-4'} />}
            {translate(config.enabled ? 'cryptoBot.overview.pause' : 'cryptoBot.overview.start')}
          </Button>

          <Button onClick={() => navigate(appRoutes.dashboard.cryptoBot.settings)} variant={'outline'}>
            <SettingsIcon className={'h-4 w-4'} />
            {translate('cryptoBot.menu.settings')}
          </Button>
        </div>
      </div>

      {config.mode === 'dry' ? (
        <div
          className={
            'rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200'
          }
        >
          {translate('cryptoBot.overview.dryNotice')}
        </div>
      ) : (
        <div className={'rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive'}>
          {translate('cryptoBot.overview.liveNotice')}
        </div>
      )}

      <div className={'grid grid-cols-2 gap-4 sm:grid-cols-4'}>
        <Stat label={translate('cryptoBot.overview.equity')} value={money(overview?.equity, quote)} />

        <Stat
          label={translate('cryptoBot.overview.return')}
          tone={retTone}
          value={overview?.totalReturnPct != null ? `${overview.totalReturnPct.toFixed(2)}%` : '—'}
        />

        <Stat
          label={translate('cryptoBot.overview.realizedPnl')}
          tone={pnlTone}
          value={money(overview?.realizedPnl, quote)}
        />

        <Stat
          label={translate('cryptoBot.overview.winRate')}
          value={overview?.winRatePct != null ? `${overview.winRatePct.toFixed(0)}%` : '—'}
        />

        <Stat
          label={translate(
            overview?.binanceBalance != null ? 'cryptoBot.overview.binanceBalance' : 'cryptoBot.overview.cash'
          )}
          value={money(overview?.binanceBalance != null ? overview.binanceBalance : overview?.cash, quote)}
        />
        {overview?.binanceGlobalValue != null && (
          <Stat
            label={translate('cryptoBot.overview.globalBalance')}
            value={money(overview.binanceGlobalValue, quote)}
          />
        )}
        <Stat label={translate('cryptoBot.overview.positionsValue')} value={money(overview?.positionsValue, quote)} />
        <Stat label={translate('cryptoBot.overview.openPositions')} value={String(overview?.openPositions ?? 0)} />
        <Stat label={translate('cryptoBot.overview.closedTrades')} value={String(overview?.closedTrades ?? 0)} />
      </div>

      {/* Realized P&L over rolling windows. */}
      <div className={'grid grid-cols-3 gap-4'}>
        <Stat
          label={translate('cryptoBot.overview.pnlDaily')}
          tone={toneOf(overview?.pnl?.daily)}
          value={money(overview?.pnl?.daily, quote)}
        />
        <Stat
          label={translate('cryptoBot.overview.pnlWeekly')}
          tone={toneOf(overview?.pnl?.weekly)}
          value={money(overview?.pnl?.weekly, quote)}
        />
        <Stat
          label={translate('cryptoBot.overview.pnlAnnual')}
          tone={toneOf(overview?.pnl?.annual)}
          value={money(overview?.pnl?.annual, quote)}
        />
      </div>

      {/* Equity evolution. */}
      <div className={'rounded-xl border border-border bg-card p-4 shadow-sm'}>
        <div className={'mb-2 flex flex-wrap items-center justify-between gap-2'}>
          <div className={'text-sm font-semibold'}>{translate('cryptoBot.overview.equityChart')}</div>

          <div className={'flex items-center gap-2'}>
            <Select onValueChange={changeTimeframe} value={timeframe}>
              <SelectTrigger className={'h-8 w-[90px]'}>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {TIMEFRAMES.map(value => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {config.mode === 'dry' && (
              <Button disabled={reset.isLoading} onClick={onReset} size={'sm'} variant={'outline'}>
                <RefreshCw className={cn('h-4 w-4', reset.isLoading && 'animate-spin')} />
                {translate('cryptoBot.overview.reset')}
              </Button>
            )}
          </div>
        </div>

        {equityData.length > 0 ? (
          <AreaTrendChart
            data={equityData}
            height={260}
            series={equitySeries}
            valueFormatter={value => money(value, quote)}
            xKey={'label'}
          />
        ) : (
          <div className={'grid place-items-center py-16 text-sm text-muted-foreground'}>
            {translate('cryptoBot.overview.noEquityData')}
          </div>
        )}
      </div>

      {/* Per-asset holdings (amounts + drift from target for rebalance). */}
      {overview?.holdings && overview.holdings.length > 0 && (
        <div className={'rounded-xl border border-border bg-card p-4 shadow-sm'}>
          <div className={'mb-2 flex items-center justify-between gap-2'}>
            <div className={'text-sm font-semibold'}>{translate('cryptoBot.overview.holdings')}</div>

            {overview.rebalanceBand != null ? (
              <span className={'text-[11px] text-muted-foreground'}>
                {translate('cryptoBot.overview.rebalanceBand', { band: overview.rebalanceBand.toFixed(1) })}
              </span>
            ) : null}
          </div>

          <div className={'grid gap-3 lg:grid-cols-3'}>
            {/* USDC — the "1" side that balances against all crypto. */}
            {usdcLive ? (
              <div className={'flex flex-col justify-center rounded-lg border border-border p-3 lg:col-span-1'}>
                <div className={'text-[10px] uppercase tracking-wide text-muted-foreground'}>{quote}</div>

                <div className={'text-lg font-semibold tabular-nums'}>{money(usdcLive.balance, quote)}</div>

                <DriftRow
                  band={overview.rebalanceBand}
                  diffPct={usdcLive.diffPct}
                  diffValue={usdcLive.diffValue}
                  quote={quote}
                />
              </div>
            ) : null}

            {/* Crypto — the "N" side: a supercard summing every asset. */}
            <div
              className={cn(
                'rounded-lg border border-border bg-muted/30 p-3',
                usdcLive ? 'lg:col-span-2' : 'lg:col-span-3'
              )}
            >
              <div className={'mb-2 flex items-center justify-between gap-2'}>
                <div className={'text-[10px] uppercase tracking-wide text-muted-foreground'}>
                  {translate('cryptoBot.overview.cryptoTotal')}
                </div>

                <div className={'flex items-baseline gap-2'}>
                  <span className={'text-lg font-semibold tabular-nums'}>{money(cryptoValue, quote)}</span>

                  {cryptoDiffPct != null ? (
                    <DriftRow
                      band={overview.rebalanceBand}
                      diffPct={cryptoDiffPct}
                      diffValue={cryptoDiffValue}
                      quote={quote}
                    />
                  ) : null}
                </div>
              </div>

              <div className={'grid grid-cols-1 gap-2 sm:grid-cols-3'}>
                {holdings.map(holding => {
                  const price = livePrices[holding.asset] ?? (holding.qty > 0 ? holding.value / holding.qty : null);

                  return (
                    <div className={'rounded-lg border border-border bg-card p-3'} key={holding.symbol}>
                      <div className={'text-[10px] uppercase tracking-wide text-muted-foreground'}>{holding.asset}</div>

                      <div className={'text-sm font-semibold tabular-nums'}>{money(holding.value, quote)}</div>

                      <div className={'text-[11px] tabular-nums text-muted-foreground'}>
                        {holding.qty.toLocaleString('en-US', { maximumFractionDigits: 8 })} {holding.asset}
                      </div>

                      {holding.diffPct != null ? (
                        <DriftRow
                          band={overview.rebalanceBand}
                          diffPct={holding.diffPct}
                          diffValue={holding.diffValue ?? 0}
                          quote={quote}
                        />
                      ) : null}

                      <div
                        className={'mt-1 border-t border-border/60 pt-1 text-[11px] tabular-nums text-muted-foreground'}
                      >
                        {price != null ? `@ ${money(price, quote)}` : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BTC market cycle (bull/bear). */}
      <div className={'rounded-xl border border-border bg-card p-4 shadow-sm'}>
        <div className={'mb-2 flex flex-wrap items-center justify-between gap-2'}>
          <div className={'text-sm font-semibold'}>{translate('cryptoBot.overview.cycle.title')}</div>

          {cycle && (
            <div className={'flex items-center gap-2'}>
              <Badge variant={cycle.current.isBull ? 'default' : 'destructive'}>
                {translate(`cryptoBot.overview.cycle.phase.${cycle.current.phase}`)}
              </Badge>

              <span className={'text-xs text-muted-foreground'}>
                {`${translate('cryptoBot.overview.cycle.drawdown')}: ${cycle.current.drawdownPct.toFixed(0)}%`}
              </span>
            </div>
          )}
        </div>

        {cycle ? (
          <>
            <CryptoBotCycleChart cycle={cycle} />

            {!!cycle.current.etaLabel && (
              <p className={'mt-2 text-xs text-muted-foreground'}>{cycle.current.etaLabel}</p>
            )}
          </>
        ) : (
          <div className={'grid place-items-center py-16'}>
            <Spinner />
          </div>
        )}
      </div>

      <div className={'grid grid-cols-2 gap-4 sm:grid-cols-4'}>
        <Stat
          label={translate('cryptoBot.settings.strategy')}
          value={translate(`cryptoBot.strategies.${config.strategy}`)}
        />
        <Stat label={translate('cryptoBot.settings.candleInterval')} value={config.candleInterval} />
        <Stat label={translate('cryptoBot.settings.quoteAsset')} value={quote} />
        <Stat label={translate('cryptoBot.settings.pairs')} value={config.baseAssets.join(', ') || '—'} />
      </div>
    </div>
  );
}

/**
 * Tone from a signed P&L value.
 */

function toneOf(value: number | null | undefined): 'pos' | 'neg' | undefined {
  if (value == null || value === 0) {
    return undefined;
  }

  return value > 0 ? 'pos' : 'neg';
}

/**
 * Drift from the rebalance target: percentage + absolute value. Highlighted in
 * amber once |drift| reaches the band — i.e. a rebalance is about to fire.
 * Positive = overweight (would sell), negative = underweight (would buy).
 */

function DriftRow({
  diffPct,
  diffValue,
  band,
  quote
}: {
  diffPct: number;
  diffValue: number;
  band: number | null;
  quote: string;
}): JSX.Element {
  const near = band != null && Math.abs(diffPct) >= band;
  const pct = `${diffPct >= 0 ? '+' : ''}${diffPct.toFixed(1)}%`;
  const abs = `${diffValue >= 0 ? '+' : '−'}${Math.abs(diffValue).toLocaleString('en-US', {
    maximumFractionDigits: 2
  })} ${quote}`;

  return (
    <div className={cn('text-[11px] tabular-nums', near ? 'font-semibold text-amber-600' : 'text-muted-foreground')}>
      {`${pct} · ${abs}`}
    </div>
  );
}
